import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Server, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const NodeSettingsNode = ({ id, data }) => {
  const { nodes = [], onUpdate } = data;
  const [expanded, setExpanded] = useState(true);
  
  // Initialize with at least one node if nodes array is empty but we have legacy data
  const nodesList = nodes.length > 0 ? nodes : 
    (data.networkaddress || data.nodeidentity) ? 
      [{ networkaddress: data.networkaddress || '', nodeidentity: data.nodeidentity || '' }] : 
      [{ networkaddress: '', nodeidentity: '' }];

  const handleAddNode = () => {
    const newNodes = [...nodesList, { networkaddress: '', nodeidentity: '' }];
    onUpdate?.({ nodes: newNodes });
  };

  const handleRemoveNode = (index) => {
    if (nodesList.length <= 1) return;
    const newNodes = nodesList.filter((_, i) => i !== index);
    onUpdate?.({ nodes: newNodes });
  };

  const handleUpdateNode = (index, field, value) => {
    const newNodes = [...nodesList];
    newNodes[index] = { ...newNodes[index], [field]: value };
    onUpdate?.({ nodes: newNodes });
  };

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-slate-800 rounded-xl border-2 border-pink-500/50 shadow-xl min-w-[320px] max-w-[360px]"
    >
      {/* Header */}
      <div 
        className="bg-gradient-to-r from-pink-500 to-rose-600 rounded-t-lg px-3 py-2 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-white/20 flex items-center justify-center">
              <Server className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-xs">Bootstrap Nodes</h3>
              <p className="text-white/70 text-[10px]">{nodesList.length} node{nodesList.length !== 1 ? 's' : ''} configured</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); handleAddNode(); }}
              className="w-6 h-6 rounded bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <Plus className="w-3 h-3 text-white" />
            </button>
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-white/70" />
            ) : (
              <ChevronDown className="w-4 h-4 text-white/70" />
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-2 space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar">
              {nodesList.map((node, index) => (
                <div key={index} className="p-2 bg-slate-900/50 rounded-lg border border-slate-700/50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500 font-medium">Node {index + 1}</span>
                    {nodesList.length > 1 && (
                      <button
                        onClick={() => handleRemoveNode(index)}
                        className="w-5 h-5 rounded bg-red-500/20 hover:bg-red-500/40 flex items-center justify-center transition-colors"
                      >
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1">Address</label>
                      <input
                        type="text"
                        value={node.networkaddress || ''}
                        onChange={(e) => handleUpdateNode(index, 'networkaddress', e.target.value)}
                        placeholder="IP:Port"
                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-xs placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-pink-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1">Identity</label>
                      <input
                        type="text"
                        value={node.nodeidentity || ''}
                        onChange={(e) => handleUpdateNode(index, 'nodeidentity', e.target.value)}
                        placeholder="Node@"
                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-xs placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-pink-500/50"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-pink-500 !border-2 !border-slate-800"
      />
    </motion.div>
  );
};

export default NodeSettingsNode;
